// Brand-aware i18n substitution for the Gestos Ágeis domain.
//
// The shared SEO i18n modules (districtI18n, equipmentI18n, regionContext) were
// written for the Orthocare Rent brand and contain literal "Orthocare Rent" /
// "orthocare.pt" references. The Gestos Ágeis domain must show its own brand, so
// every exported i18n value is wrapped with `brandWrap` / `brandText`: on the
// Gestos Ágeis domain the brand tokens are substituted at runtime; on the
// Orthocare domains the values are returned untouched (same references, same
// behaviour), so the Orthocare content stays exactly as it was.

import { isGestosAgeisDomain } from '@/lib/siteDomain';

export const GESTOS_BRAND = 'Gestos Ágeis';
export const GESTOS_DOMAIN = 'gestosageis.pt';
export const ORTHOCARE_BRAND = 'Orthocare Rent';
export const ORTHOCARE_SHORT = 'Orthocare';

const SUBSTITUTIONS = [
  ['Orthocare Rent', GESTOS_BRAND],
  ['orthocare.pt', GESTOS_DOMAIN],
];

export function brandText(value) {
  if (typeof value !== 'string') return value;
  if (!isGestosAgeisDomain()) return value;
  let result = value;
  for (const [from, to] of SUBSTITUTIONS) {
    result = result.split(from).join(to);
  }
  return result;
}

// Recursively wrap strings, arrays, objects and functions so every string that
// flows through an i18n export gets brand-substituted on the Gestos Ágeis domain.
// On non-Gestos-Ágeis domains the input is returned unchanged.
export function brandWrap(value) {
  if (!isGestosAgeisDomain()) return value;
  return wrapDeep(value);
}

function wrapDeep(value) {
  if (typeof value === 'string') return brandText(value);
  if (typeof value === 'function') {
    return (...args) => wrapDeep(value(...args));
  }
  if (Array.isArray(value)) return value.map(wrapDeep);
  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value)) out[key] = wrapDeep(value[key]);
    return out;
  }
  return value;
}

// Brand display name for page titles / JSON-LD schema.
export function brandName() {
  return isGestosAgeisDomain() ? GESTOS_BRAND : ORTHOCARE_BRAND;
}

// Short brand label for breadcrumbs.
export function brandShort() {
  return isGestosAgeisDomain() ? GESTOS_BRAND : ORTHOCARE_SHORT;
}